import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useJobListing, useCreateJobListing, useUpdateJobListing } from "./queries";
import { LangToggle, type Lang } from "../../components/Editor/LangToggle";
import { RichTextEditor } from "../../components/Content/RichTextEditor";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { checkRequired, isBlank, isBlankHtml } from "@/lib/form-validation";
import { errorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

export default function JobForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: job, isLoading } = useJobListing(id ?? "");
  const createMutation = useCreateJobListing();
  const updateMutation = useUpdateJobListing();

  const [lang, setLang] = useState<Lang>("tr");

  // Form state
  const [title, setTitle] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [department, setDepartment] = useState("");
  const [departmentEn, setDepartmentEn] = useState("");
  const [location, setLocation] = useState("İstanbul");
  const [locationEn, setLocationEn] = useState("");
  const [type, setType] = useState("Tam Zamanlı");
  const [typeEn, setTypeEn] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [active, setActive] = useState(true);

  // Kaydedilmemiş değişiklik takibi: yüklenen ilk değerlerle karşılaştırılır.
  const [loaded, setLoaded] = useState(!isEdit);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const snapshot = JSON.stringify({
    title,
    titleEn,
    department,
    departmentEn,
    location,
    locationEn,
    type,
    typeEn,
    // Boş editör "" ya da "<p></p>" olabilir; ikisi de aynı sayılır
    description: isBlankHtml(description) ? "" : description,
    descriptionEn: isBlankHtml(descriptionEn) ? "" : descriptionEn,
    active,
  });
  // İlk değerler yüklendiği render'da taban alınır (effect yerine render sırasında)
  if (loaded && baseline === null) setBaseline(snapshot);

  // Populate on edit
  useEffect(() => {
    if (job) {
      setTitle(job.title ?? "");
      setTitleEn(job.titleEn ?? "");
      setDepartment(job.department ?? "");
      setDepartmentEn(job.departmentEn ?? "");
      setLocation(job.location ?? "İstanbul");
      setLocationEn(job.locationEn ?? "");
      setType(job.type ?? "Tam Zamanlı");
      setTypeEn(job.typeEn ?? "");
      setDescription(job.description ?? "");
      setDescriptionEn(job.descriptionEn ?? "");
      setActive(job.active ?? true);
      setLoaded(true);
      setBaseline(null);
    }
  }, [job]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const valid = checkRequired(
      [
        { label: "Pozisyon", missing: isBlank(title), target: { lang: "tr", id: "title" } },
        {
          label: "Departman",
          missing: isBlank(department),
          target: { lang: "tr", id: "department" },
        },
        {
          label: "Açıklama",
          missing: isBlankHtml(description),
          target: { lang: "tr", id: "description" },
        },
      ],
      (t) => {
        if (t.lang) setLang(t.lang);
      },
    );
    if (!valid) return;
    try {
      const payload = {
        title,
        titleEn: titleEn || null,
        department,
        departmentEn: departmentEn || null,
        location,
        locationEn: locationEn || null,
        type,
        typeEn: typeEn || null,
        description,
        descriptionEn: descriptionEn || null,
        active,
      };

      if (isEdit) {
        await updateMutation.mutateAsync({ id: id!, ...payload });
        toast.success("İş ilanı güncellendi");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("İş ilanı oluşturuldu");
      }
      setSaved(true);
      navigate("/jobs");
    } catch (error: unknown) {
      toast.error(isAxiosError(error) ? errorMessage(error, "Bir hata oluştu") : "Bir hata oluştu");
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const dirty = baseline !== null && snapshot !== baseline && !saved;
  const { confirmNavigate, dialog: unsavedDialog } = useUnsavedChanges(dirty);

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => confirmNavigate("/jobs")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">{isEdit ? "İş İlanı Düzenle" : "Yeni İş İlanı"}</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form id="job-form" noValidate onSubmit={handleSubmit} className="space-y-5">
            {/* Lang toggle */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Dil:</span>
              <LangToggle value={lang} onChange={setLang} />
            </div>

            {/* title */}
            {lang === "tr" ? (
              <div className="space-y-1.5">
                <Label htmlFor="title">
                  Pozisyon <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="titleEn">Pozisyon (English)</Label>
                <Input id="titleEn" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} />
              </div>
            )}

            {/* department */}
            {lang === "tr" ? (
              <div className="space-y-1.5">
                <Label htmlFor="department">
                  Departman <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  required
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="departmentEn">Departman (English)</Label>
                <Input
                  id="departmentEn"
                  value={departmentEn}
                  onChange={(e) => setDepartmentEn(e.target.value)}
                />
              </div>
            )}

            {/* location */}
            {lang === "tr" ? (
              <div className="space-y-1.5">
                <Label htmlFor="location">Lokasyon</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="locationEn">Lokasyon (English)</Label>
                <Input
                  id="locationEn"
                  value={locationEn}
                  onChange={(e) => setLocationEn(e.target.value)}
                />
              </div>
            )}

            {/* type */}
            {lang === "tr" ? (
              <div className="space-y-1.5">
                <Label htmlFor="type">Çalışma Türü</Label>
                <Input
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  placeholder="Tam Zamanlı"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="typeEn">Çalışma Türü (English)</Label>
                <Input
                  id="typeEn"
                  value={typeEn}
                  onChange={(e) => setTypeEn(e.target.value)}
                  placeholder="Full Time"
                />
              </div>
            )}

            {/* description */}
            {lang === "tr" ? (
              <div className="space-y-1.5">
                <Label>
                  Açıklama <span className="text-destructive">*</span>
                </Label>
                <RichTextEditor id="description" value={description} onChange={setDescription} />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Açıklama (English)</Label>
                <RichTextEditor value={descriptionEn} onChange={setDescriptionEn} />
              </div>
            )}

            {/* active */}
            <div className="flex items-center gap-3">
              <Switch id="active" checked={active} onCheckedChange={setActive} />
              <Label htmlFor="active">Aktif İlan</Label>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Submit bar */}
      <div className="flex gap-2 pt-2">
        <Button form="job-form" type="submit" disabled={isPending}>
          {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
        </Button>
        <Button variant="outline" onClick={() => confirmNavigate("/jobs")}>
          İptal
        </Button>
      </div>
      {unsavedDialog}
    </div>
  );
}
